/**
 * QuietRewardsContext — β.10.3 Quiet Rewards toggle.
 *
 * When quietMode is ON, gamification consumers should suppress animations,
 * sounds, and badge pop-ups. The value persists to localStorage so the user's
 * preference survives refresh.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

const LS_KEY = "teamblob-quiet-rewards";

interface QuietRewardsContextValue {
  quietMode: boolean;
  setQuietMode: (value: boolean) => void;
}

const QuietRewardsContext = createContext<QuietRewardsContextValue | null>(
  null,
);

export function QuietRewardsProvider({ children }: { children: ReactNode }) {
  const [quietMode, setQuietModeState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_KEY) === "true";
    } catch {
      return false;
    }
  });

  // Keep localStorage in sync whenever the value changes.
  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, quietMode ? "true" : "false");
    } catch {
      // localStorage unavailable (private browsing, storage quota) — ignore.
    }
  }, [quietMode]);

  const setQuietMode = useCallback((value: boolean) => {
    setQuietModeState(value);
  }, []);

  return (
    <QuietRewardsContext.Provider value={{ quietMode, setQuietMode }}>
      {children}
    </QuietRewardsContext.Provider>
  );
}

export function useQuietRewards(): QuietRewardsContextValue {
  const ctx = useContext(QuietRewardsContext);
  if (ctx === null) {
    throw new Error(
      "useQuietRewards must be used within <QuietRewardsProvider>",
    );
  }
  return ctx;
}
